'use client';

import { DeliverySearch, MinimalGeocodeResult } from './DeliverySearch';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';

import { ApplicationConfigContext } from '@/components/common/ApplicationConfigProvider/ApplicationConfigProvider';
import { AuthContext } from '@/components/common/AuthProvider/AuthProvider';
import { CartContext } from '@/app/(ordering)/shop/CartProvider/CartProvider';
import DateTimeComponent from '@/components/ordering/DateTimeComponent/DateTimeComponent';
import { DeliveryAddressForm } from '@/components/ordering/DeliveryAddressForm/DeliveryAddressForm';
import { DeliverySelection } from './DeliverySelection';
import { Loading } from '@/components/common/Icons/Loading';
import type { deliveryAddressProps } from '../../data/shopdata.type';
import { getDeliveryAddress } from '../../data/shopdata';

export type DeliveryCurrentView = 'dateTime' | 'searching' | 'selection' | 'deliveryForm';

type DeliveryPanelProps = {
	closeModal: Dispatch<SetStateAction<void>>;
	isModalOpen: boolean;
	redirectedFrom?: string;
};
export function DeliveryPanel({ closeModal, isModalOpen, redirectedFrom }: DeliveryPanelProps) {
	const { userDetails } = useContext(AuthContext);
	const { address, apiCart, firstCartFetchFinished, isFromCampaign, campaignName } =
		useContext(CartContext);
	const appConfigContext = useContext(ApplicationConfigContext);

	// State concerns
	const [deliveryAddress, setDeliveryAddress] = useState<deliveryAddressProps>();
	const [selectedAddress, setSelectedAddress] = useState<MinimalGeocodeResult[] | undefined>();

	//UI Concern
	const [currentView, setCurrentView] = useState<DeliveryCurrentView>('selection');
	const [isLoading, setIsLoading] = useState(false);
	const [isAddressAvailable, setIsAddressAvailable] = useState(false);

	const [imageSrc, setImageSrc] = useState<string | null>(null);
	const [isImageLoaded, setIsImageLoaded] = useState(false);

	useEffect(() => {
		const loadPage = () => {
			setIsLoading(true);
			if (apiCart?.deliveryAddress || address.deliveryAddress) {
				if (apiCart?.deliveryAddress) {
					setDeliveryAddress({
						id: -1,
						lastPickupStoreNumber: -1,
						lastDeliveryAddress: {
							...apiCart.deliveryAddress,
							building: apiCart.deliveryAddress.building ?? '',
							specialInstructions: apiCart.deliveryAddress.specialInstructions ?? '',
						},
					});
				} else {
					setDeliveryAddress({
						id: -1,
						lastPickupStoreNumber: -1,
						lastDeliveryAddress: {
							streetAddress: address.deliveryAddress?.streetAddress ?? '',
							building: '',
							city: address.deliveryAddress?.city ?? '',
							state: address.deliveryAddress?.state ?? '',
							zipCode: address.deliveryAddress?.zipCode ?? '',
							specialInstructions: address.deliveryAddress?.specialInstructions ?? '',
						},
					});
				}
				setIsAddressAvailable(true);
				setIsLoading(false);
			} else if (userDetails) {
				getDeliveryAddress(userDetails.customerId)
					.then((response) => {
						if (response.lastDeliveryAddress === null) {
							setIsAddressAvailable(false);
						} else {
							setDeliveryAddress(response);
							setIsAddressAvailable(true);
						}
					})
					.catch(() => {
						setIsAddressAvailable(false);
					})
					.finally(() => {
						setIsLoading(false);
					});
			} else if (firstCartFetchFinished) {
				setIsAddressAvailable(false);
				setIsLoading(false);
			}
		};
		if (isModalOpen) {
			loadPage();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userDetails, apiCart, firstCartFetchFinished, isModalOpen]);

	const callBackForNavigateToRecentSearch = () => {
		setCurrentView('searching');
	};

	useEffect(() => {
		if (redirectedFrom === 'review') {
			setCurrentView('searching');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const checkIfCartEmpty = !!apiCart;
		if (!checkIfCartEmpty) return;

		localStorage.setItem(
			'storeCoordinates',
			JSON.stringify({
				latitude: apiCart?.restaurant.latitude,
				longitude: apiCart?.restaurant.longitude,
			})
		);
	}, [apiCart]);

	useEffect(() => {
		if (isFromCampaign && campaignName) {
			const url = `${appConfigContext?.FACEBOOK_LTO_IMAGE_URL}/${campaignName}.png`;
			const img = new Image();

			img.onload = () => {
				setImageSrc(url);
				setIsImageLoaded(true);
			};
			img.onerror = () => {
				setIsImageLoaded(false);
			};

			img.src = url;
		}
	}, [isFromCampaign, campaignName, appConfigContext?.FACEBOOK_LTO_IMAGE_URL]);

	return (
		<>
			{isLoading ? (
				<div className="flex justify-center">
					<Loading size="large" fill="green" />
				</div>
			) : (
				<>
					{isFromCampaign &&
						currentView !== 'searching' &&
						currentView !== 'deliveryForm' &&
						currentView !== 'dateTime' &&
						isImageLoaded &&
						imageSrc && (
							<img
								className="aspect-[16/9] h-auto w-full rounded-[10px] object-center md:max-h-[280px] xl:max-h-[275px]"
								src={imageSrc}
								alt="LTO image"
								height={44}
								width={500}
								fetchPriority="high"
							/>
						)}

					{currentView === 'selection' && (
						<DeliverySelection
							closeModal={closeModal}
							setCurrentView={setCurrentView}
							isAddressAvailable={isAddressAvailable}
							deliveryAddress={deliveryAddress}
							setDeliveryAddress={setDeliveryAddress}
						/>
					)}

					{currentView === 'searching' && (
						<DeliverySearch
							setCurrentView={setCurrentView}
							isAddressAvailable={isAddressAvailable}
							deliveryAddress={deliveryAddress}
							setDeliveryAddress={setDeliveryAddress}
							setSelectedAddress={setSelectedAddress}
						/>
					)}

					{currentView === 'deliveryForm' && (
						<DeliveryAddressForm
							setCurrentView={setCurrentView}
							selectedAddress={selectedAddress}
							setDeliveryAddress={setDeliveryAddress}
							onBack={callBackForNavigateToRecentSearch}
							setIsAddressAvailable={setIsAddressAvailable}
							userDetails={userDetails}
						/>
					)}

					{currentView === 'dateTime' && (
						<DateTimeComponent
							orderType="Delivery"
							onCloseDateTime={() => setCurrentView('selection')}
						/>
					)}
				</>
			)}
		</>
	);
}
